import axios from 'axios';
import { msalInstance, apiTokenRequest } from './authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { config } from '../config/config';
import guardAuthService from './guardAuth.service';

const api = axios.create({
  baseURL: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * The cached theme the index.html bootstrap paints before React exists.
 *
 * Cleared alongside the MSAL cache: it is keyed by owner INSIDE the blob rather
 * than in the key (the bootstrap runs before MSAL, so it cannot namespace), and
 * the engine only validates the owner once MSAL resolves. On a shared browser
 * that leaves the next person seeing the previous user's theme for one paint.
 * Dropping it at sign-out closes that window.
 *
 * Kept here rather than imported from the theme module so the auth path has no
 * dependency on theming - see theme/ThemeEngineContext.ts for the writer.
 */
const THEME_BOOTSTRAP_KEY = 'theme_bootstrap';

// Helper to clear MSAL cache from localStorage
const clearMsalCache = () => {
  try {
    // MSAL stores tokens in localStorage with keys containing the client ID
    // Clear all MSAL-related entries to force fresh login
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('msal') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    localStorage.removeItem(THEME_BOOTSTRAP_KEY);
  } catch (e) {
    console.error('Error clearing MSAL cache:', e);
  }
};

// Track if we're already redirecting to prevent loops
let isRedirecting = false;

type LogoutReason = 'session_expired' | 'force_logout';

// Helper function to redirect to login with proper cache clearing
const redirectToLogin = (reason: LogoutReason = 'session_expired') => {
  // Prevent multiple simultaneous redirects
  if (isRedirecting) {
    return;
  }

  // Check for redirect loop - if we redirected in the last 5 seconds, don't redirect again
  const lastRedirectTime = sessionStorage.getItem('lastAuthRedirect');
  const now = Date.now();
  if (lastRedirectTime && (now - parseInt(lastRedirectTime, 10)) < 5000) {
    console.warn('Auth redirect loop detected, clearing all auth state');
    // Clear everything and go to login without further redirects
    sessionStorage.removeItem('lastAuthRedirect');
    // Clear guard auth so guard tokens don't survive the bounce
    guardAuthService.logout();
    // Clear MSAL localStorage entries to force fresh login
    clearMsalCache();
    msalInstance.setActiveAccount(null);
    window.location.replace(`/login?${reason}=true`);
    return;
  }

  isRedirecting = true;
  sessionStorage.setItem('lastAuthRedirect', now.toString());

  // Clear guard auth so guard tokens don't survive a session-expired bounce
  guardAuthService.logout();

  // Clear MSAL cache to force fresh login
  clearMsalCache();
  msalInstance.setActiveAccount(null);

  // Use replace to prevent back button returning to broken state
  window.location.replace(`/login?${reason}=true`);
};

// --- Admin force sign-out ---------------------------------------------------
// An admin can mark a user's tokens dead (Administration > Users > Force
// Sign-Out). The backend then 401s with error="force_logout" on every request.
// Clearing app state alone would not be enough: MSAL's acquireTokenSilent hands
// back the same cached access token until it expires, so the only way out of the
// 401 is to drop the MSAL cache and re-authenticate — which redirectToLogin
// already does. The user normally lands back in via SSO without a password.

export const FORCE_LOGOUT_EVENT = 'force-logout';
// Grace period so the user can save in-flight work before being bounced.
export const FORCE_LOGOUT_GRACE_MS = 15_000;

export interface ForceLogoutDetail {
  message: string;
  graceMs: number;
}

let forceLogoutStarted = false;

// Completes the sign-out immediately. Exported for the banner's "Sign out now".
export const completeForceLogout = () => redirectToLogin('force_logout');

const beginForceLogout = (message: string) => {
  // Several requests can be in flight when the kick lands; only the first
  // starts the countdown, so the user sees one banner rather than six.
  if (forceLogoutStarted) {
    return;
  }
  forceLogoutStarted = true;

  window.dispatchEvent(
    new CustomEvent<ForceLogoutDetail>(FORCE_LOGOUT_EVENT, {
      detail: { message, graceMs: FORCE_LOGOUT_GRACE_MS },
    })
  );

  // Backstop: the banner normally drives the redirect, but the sign-out must
  // still happen if no listener is mounted — otherwise the user would sit on a
  // page silently 401ing on everything.
  window.setTimeout(completeForceLogout, FORCE_LOGOUT_GRACE_MS);
};

// Request interceptor to add Bearer token
api.interceptors.request.use(
  async (config) => {
    // IMPORTANT: Add Production Readonly Mode header FIRST, before any returns
    // This must run for ALL requests when the mode is enabled
    const productionReadonlyMode = localStorage.getItem('productionReadonlyMode') === 'true';
    if (productionReadonlyMode) {
      config.headers['X-Production-Readonly-Mode'] = 'true';
    }

    // Get Azure AD token for API access
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts && accounts.length > 0) {
        const request = {
          ...apiTokenRequest,
          account: accounts[0],
        };
        const response = await msalInstance.acquireTokenSilent(request);
        if (response.accessToken) {
          config.headers.Authorization = `Bearer ${response.accessToken}`;
          return config;
        }
      }
    } catch (error) {
      // Handle MSAL-specific token expiration errors
      if (error instanceof InteractionRequiredAuthError) {
        console.log('Token expired or consent required, redirecting to login');
        redirectToLogin();
        return Promise.reject(new Error('Token expired'));
      }
      console.error('Error acquiring token:', error);
    }

    // Guard-token fallback: only when MSAL has no account at all
    const guardToken = guardAuthService.getToken();
    if (guardToken) {
      config.headers.Authorization = `Bearer ${guardToken}`;
      return config;
    }

    // No token available - let the request proceed, backend will return 401
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track 401 errors to detect expired sessions
let consecutive401Errors = 0;
const MAX_CONSECUTIVE_401_ERRORS = 3;

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Reset 401 counter on successful response
    consecutive401Errors = 0;
    return response;
  },
  (error) => {
    // Check endpoint types - used for both 401 and 404 handling
    const isSamsaraEndpoint = error.config?.url?.includes('/samsara');
    const isUserSettingsEndpoint = error.config?.url?.includes('/settings/user-settings');
    const isImpersonateEndpoint = error.config?.url?.includes('/impersonate');
    const isPermissionsEndpoint = error.config?.url?.includes('/permissions');
    const isConfigEndpoint = error.config?.url?.includes('/config');
    const isFeatureRequestEndpoint = error.config?.url?.includes('/feature-requests');
    const isBugReportEndpoint = error.config?.url?.includes('/bug-reports');
    // Leading slash matters: must not match /git-activity
    const isActivityEndpoint = error.config?.url?.includes('/activity/');

    // Handle 401 Unauthorized (expired or invalid token)
    if (error.response?.status === 401) {
      consecutive401Errors++;

      // `detail` is a plain string on most 401s but an object on the
      // force-logout 401, so normalize before any string work below.
      const rawDetail = error.response?.data?.detail;
      const errorDetail = typeof rawDetail === 'string'
        ? rawDetail
        : (rawDetail?.message || '');

      // An admin signed this user out. Checked FIRST, and ahead of
      // skipRedirectEndpoints: /permissions is on that skip list and
      // PermissionContext polls it constantly, so a force-logout arriving on
      // that endpoint would otherwise be swallowed and never acted on.
      if (rawDetail?.error === 'force_logout') {
        consecutive401Errors = 0;
        beginForceLogout(errorDetail || 'An administrator signed you out');
        return Promise.reject(error);
      }

      const isTokenExpired = errorDetail.toLowerCase().includes('expired') ||
                             errorDetail.toLowerCase().includes('signature');

      // If token is explicitly expired, redirect immediately
      if (isTokenExpired) {
        console.log('Token expired (backend validation failed), redirecting to login');
        consecutive401Errors = 0;
        redirectToLogin();
        return Promise.reject(error);
      }

      // If we get multiple 401s in a row, force logout regardless of endpoint
      if (consecutive401Errors >= MAX_CONSECUTIVE_401_ERRORS) {
        console.log(`Received ${consecutive401Errors} consecutive 401 errors, forcing logout`);
        consecutive401Errors = 0; // Reset counter
        redirectToLogin();
        return Promise.reject(error);
      }

      // Skip redirect for endpoints that may legitimately fail during auth transition
      const skipRedirectEndpoints = isSamsaraEndpoint || isImpersonateEndpoint || isPermissionsEndpoint ||
                                     isUserSettingsEndpoint || isConfigEndpoint || isFeatureRequestEndpoint || isBugReportEndpoint ||
                                     isActivityEndpoint;

      if (!skipRedirectEndpoints) {
        console.log('Received 401 Unauthorized, redirecting to login');
        consecutive401Errors = 0; // Reset counter since we're redirecting
        redirectToLogin();
      }
    } else {
      // Reset 401 counter on non-401 errors
      consecutive401Errors = 0;
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      // Check if this is a Production Readonly Mode block
      if (error.response?.data?.error === 'production_readonly_mode') {
        const blockedRequest = error.response.data.blocked_request;
        // Dispatch custom event for toast notification (api.ts can't use React hooks)
        window.dispatchEvent(new CustomEvent('production-readonly-blocked', {
          detail: blockedRequest
        }));
        // Don't override error message - let the UI handle it via the event
        return Promise.reject(error);
      }

      // Regular 403 - insufficient permissions
      const endpoint = error.config?.url?.replace('/api/v1', '') || 'this resource';
      const method = (error.config?.method || 'GET').toUpperCase();
      console.log('Received 403 Forbidden - insufficient permissions for:', endpoint);

      // Enhance error message for better user experience
      const backendMessage = error.response?.data?.detail || '';
      const userFriendlyMessage = backendMessage || `You are not authorized to access ${endpoint}. Please contact your administrator if you believe this is an error.`;

      // Override the error message
      error.message = userFriendlyMessage;
      if (error.response) {
        error.response.data = {
          ...error.response.data,
          message: userFriendlyMessage,
          userMessage: userFriendlyMessage
        };
      }

      // Surface to the user via sticky toast (api.ts can't use React hooks).
      // Skip the telemetry endpoints themselves so a failure there can't loop.
      if (!endpoint.startsWith('/permission-denied-log') && !endpoint.startsWith('/activity/')) {
        window.dispatchEvent(new CustomEvent('permission-denied', {
          detail: { method, path: endpoint, message: userFriendlyMessage }
        }));
      }
    }

    // Suppress console logging for expected 404s on user settings
    if (error.response?.status === 404 && isUserSettingsEndpoint) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
